import { type ChangeEvent, type FC, useId, useRef, useState } from 'react';

import { FileText, Image, Paperclip, X } from 'lucide-react';

import styles from './AttachmentsField.module.css';
import {
  addAttachments,
  ATTACHMENT_ACCEPT,
  attachmentType,
  formatSize,
  MAX_ATTACHMENTS,
} from '../lib/attachments';

interface AttachmentsFieldProps {
  files: readonly File[];
  onChange: (files: File[]) => void;
  disabled?: boolean;
}

// Имя без последних символов и сами они (≈ «…_финальная» + «.pdf»): многоточие ставится в
// середине, а конец имени с расширением не обрезается
const splitName = (name: string) => {
  const dot = name.lastIndexOf('.');
  const tail = dot > 0 ? Math.max(name.length - dot, 6) : 6;
  return { start: name.slice(0, -tail), end: name.slice(-tail) };
};

/** Файлы к заявке (PDF и изображения). Отправляются вместе с ней письмом */
export const AttachmentsField: FC<AttachmentsFieldProps> = ({ files, onChange, disabled }) => {
  const id = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const result = addAttachments(files, [...(event.target.files ?? [])]);
    onChange(result.files);
    setError(result.error);
    // Сброс: тот же файл, убранный из списка, можно выбрать снова
    event.target.value = '';
  };

  const remove = (file: File) => {
    onChange(files.filter((f) => f !== file));
    setError(null);
  };

  return (
    <div className={styles.root} role="group" aria-labelledby={`${id}-heading`}>
      <span className={styles.heading} id={`${id}-heading`}>
        Файлы
      </span>
      <input
        className={styles.nativeInput}
        ref={inputRef}
        type="file"
        multiple
        accept={ATTACHMENT_ACCEPT}
        onChange={handleSelect}
        tabIndex={-1}
        aria-hidden
      />
      <button
        className={styles.addButton}
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={disabled || files.length >= MAX_ATTACHMENTS}
        aria-describedby={`${id}-hint`}
      >
        <Paperclip size={18} aria-hidden /> Прикрепить файлы
      </button>
      <p className={styles.hint} id={`${id}-hint`}>
        PDF, JPG или PNG — до {MAX_ATTACHMENTS} файлов, всего до 10 МБ
      </p>
      {/* Причину отказа объявляет скринридер: файл не добавился, а фокус остался на кнопке */}
      <div aria-live="polite">{error && <p className={styles.errorText}>{error}</p>}</div>
      {files.length > 0 && (
        <ul className={styles.list} aria-label="Прикреплённые файлы">
          {files.map((file) => (
            <li className={styles.item} key={`${file.name}-${file.size}-${file.lastModified}`}>
              <span className={styles.kind} aria-hidden>
                {attachmentType(file) === 'application/pdf' ? (
                  <FileText size={16} />
                ) : (
                  <Image size={16} />
                )}
              </span>
              <span className={styles.name} title={file.name}>
                <span className={styles.nameStart}>{splitName(file.name).start}</span>
                {splitName(file.name).end}
              </span>
              <span className={styles.size}>{formatSize(file.size)}</span>
              <button
                className={styles.remove}
                type="button"
                onClick={() => remove(file)}
                disabled={disabled}
                aria-label={`Убрать файл ${file.name}`}
              >
                <X size={16} aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
